import { Icon16Checkmark, Typography } from "@cur8/maneki";
import { Measurement } from "@cur8/rich-entity";
import { CAUTION_TITLE } from "./measurementCaution";
import { MetricConfig } from "./measurementConfig";
import { getEffectiveConversion } from "./measurementConversion";
import {
  formatMeasurementValue,
  formatUnitPlaceholder,
} from "./measurementFormat";
import { isImplausibleReading } from "./measurementPlausibility";
import styles from "./styles.module.sass";

interface MeasurementTileProps {
  config: MetricConfig;
  measurement: Measurement | undefined;
  countryCode: string | undefined;
  unitOverrides: Map<string, boolean>;
  // Off until the retest alerts flag is on.
  flagImplausible?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

export function MeasurementTile({
  config,
  measurement,
  countryCode,
  unitOverrides,
  flagImplausible,
  disabled,
  onClick,
}: MeasurementTileProps) {
  // Not a finished measurement, so it takes the caution treatment rather than
  // reading as done.
  const isImplausible =
    flagImplausible === true && isImplausibleReading(config, measurement);
  const isDone = measurement != null && !isImplausible;
  const convDef = getEffectiveConversion(
    config,
    countryCode,
    unitOverrides.get(config.metricName)
  );
  const displayValue = formatMeasurementValue(config, measurement, convDef);

  return (
    <button
      type="button"
      className={styles.tile}
      data-done={isDone || undefined}
      data-caution={isImplausible || undefined}
      data-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {isImplausible ? (
        // No triangle: the group tile above carries it for the pair. Reuses the
        // group tile's classes so the eyebrow sits at the same offset.
        <>
          <span className={styles.cautionEyebrow}>{CAUTION_TITLE}</span>
          <span className={styles.cautionTileLabel}>{config.label}</span>
        </>
      ) : (
        <div className={styles.tileHeader}>
          {/* No color prop: Typography emits no data-color, so the title
              inherits the inverse white that data-done sets on a teal tile. */}
          <Typography variant="label-s" color={disabled ? "subtle" : undefined}>
            {config.label}
          </Typography>
          {isDone && (
            <span className={styles.tileCheck}>
              <Icon16Checkmark />
            </span>
          )}
        </div>
      )}
      {isImplausible ? (
        <span className={styles.tileValueFlagged} data-implausible>
          {displayValue ?? formatUnitPlaceholder(config, convDef)}
        </span>
      ) : (
        <Typography variant="body-s" color={isDone ? "inverted" : "subtle"}>
          {displayValue ?? formatUnitPlaceholder(config, convDef)}
        </Typography>
      )}
    </button>
  );
}
